import leaflet from 'leaflet';

const PATCHED_FLAG = '__setIconUnchangedGuard';

/**
 * Patches `leaflet.Marker.prototype.setIcon` once (idempotent) so that
 * re-setting the **same** `DivIcon` object becomes a no-op — preserving the
 * marker's existing `<img>` DOM and avoiding a network refetch.
 *
 * Why this is needed:
 *
 * ember-leaflet binds `<MarkerLayer @icon>` via a `{{did-update}}` whose
 * trigger is tag-based, not value-based. On the live map, our `@icon`
 * expression's `html` input reads `liveMap.resourceStates` (replaced
 * wholesale on every poll), so the modifier re-fires every poll — even when
 * our DivIcon object cache returns the same instance. Leaflet's `setIcon` is
 * unconditional: it always runs `createIcon`, which resets `innerHTML` and
 * **parses a brand-new `<img>` node from the HTML string**. Under
 * `Cache-Control: no-cache` (which our S3 avatar responses currently carry)
 * that new node triggers a fresh network fetch.
 *
 * The guard short-circuits **only** when the marker's current icon DOM is
 * already this exact icon object (`this._icon && this.options.icon === icon`).
 * That condition is only met when our `getCachedDivIcon` returns a cached
 * instance — direct callers in the app (`resource-map/area/component.js`)
 * always pass a freshly-constructed `new leaflet.DivIcon(...)`, so the guard
 * never affects them.
 */
/**
 * After a state-change rebuild (legitimate cache MISS → setIcon → fresh
 * DOM), the new `<div data-marker-scale>` wrapper starts with no transform
 * and would briefly render at scale 1 until the next zoom event re-applies
 * the correct scale.
 *
 * To avoid that flash — and, importantly, to handle transitions where the
 * "correct" scale depends on the marker's *new* state (e.g. bulk-reselect
 * at a different zoom needs the bulk scale, not the previously-stashed
 * non-selected scale) — the component stamps a `__refreshMarkerScale`
 * callback on each marker. The guard invokes it after every rebuild so the
 * scale is recomputed against current state instead of read from a stale
 * stash.
 *
 * `__currentMarkerScale` remains as a defensive fallback for any marker
 * that has a wrapper but no refresh callback (shouldn't happen in normal
 * operation, but the guard runs for every Leaflet marker, so it has to
 * tolerate the case).
 */
function restoreMarkerScaleAfterRebuild(marker) {
  if (!marker._icon) return;
  const wrapper = marker._icon.querySelector('[data-marker-scale]');
  if (!wrapper) return;
  const refresh = marker.__refreshMarkerScale;
  if (typeof refresh === 'function') {
    refresh();
    return;
  }
  const scale = marker.__currentMarkerScale;
  if (scale != null) {
    wrapper.style.transform = `scale(${scale})`;
  }
}

export function installLeafletMarkerSetIconGuard() {
  if (leaflet.Marker.prototype[PATCHED_FLAG]) return;
  const origSetIcon = leaflet.Marker.prototype.setIcon;
  leaflet.Marker.prototype.setIcon = function (icon) {
    if (this._icon && this.options.icon === icon) {
      return this;
    }
    const result = origSetIcon.call(this, icon);
    restoreMarkerScaleAfterRebuild(this);
    return result;
  };
  leaflet.Marker.prototype[PATCHED_FLAG] = true;
}

installLeafletMarkerSetIconGuard();
